declare var L: any;

namespace Serenity {
    import Option = Serenity.Decorators.option;
    import Element = Decorators.element;

    export namespace EditorTypeRegistry {
        let knownTypes: Q.Dictionary<WidgetClass>;

        export function get(key: string): WidgetClass {
            if (Q.isEmptyOrNull(key)) {
                throw new (ss as any).ArgumentNullException("key");
            }

            initialize();

            var editorType = knownTypes[key.toLowerCase()];

            if (editorType == null) {
                var type = Q.typeByFullName(key);

                if (type != null) {
                    knownTypes[key.toLowerCase()] = type;

                    return type;
                }

                throw new ss.Exception(Q.format("Can't find {0} editor type!", key));
            }

            return editorType;
        }

        function initialize(): void {
            if (knownTypes != null) {
                return;
            }

            knownTypes = {};

            var assemblies = (ss as any).getAssemblies();

            for (var assembly of assemblies) {
                for (var type of (ss as any).getAssemblyTypes(assembly)) {
                    if (!(type.prototype instanceof Serenity.Widget)) {
                        continue;
                    }

                    if ((ss as any).isGenericTypeDefinition(type)) {
                        continue;
                    }

                    var fullName = (ss as any).getTypeFullName(type).toLowerCase();
                    knownTypes[fullName] = type;

                    var editorAttr = (ss as any).getAttributes(type, Serenity.EditorAttribute, false);

                    if (editorAttr != null && editorAttr.length) {
                        var attrKey = editorAttr[0].key;

                        if (!Q.isEmptyOrNull(attrKey)) {
                            knownTypes[attrKey.toLowerCase()] = type;
                        }
                    }

                    for (var k of Q.Config.rootNamespaces) {
                        if (Q.startsWith(fullName, `${k.toLowerCase()}.`)) {
                            var kx = fullName.substr(k.length + 1).toLowerCase();

                            if (knownTypes[kx] == null) {
                                knownTypes[kx] = type;
                            }
                        }
                    }
                }
            }

            setTypeKeysWithoutEditorSuffix();
        }

        export function reset(): void {
            knownTypes = null;
        }

        function setTypeKeysWithoutEditorSuffix() {
            var suffix = "editor";
            var keys = Object.keys(knownTypes);

            for (var k of keys) {
                setWithoutSuffix(k, knownTypes[k]);
            }
        }

        function setWithoutSuffix(key: string, t: Serenity.WidgetClass) {
            var suffix = "editor";

            if (!Q.endsWith(key, suffix)) {
                return;
            }

            var p = key.substr(0, key.length - suffix.length);

            if (Q.isEmptyOrNull(p)) {
                return;
            }

            if (knownTypes[p] != null) {
                return;
            }

            knownTypes[p] = knownTypes[key];
        }
    }

    export namespace EditorUtils {
        export function getDisplayText(editor: Serenity.Widget<any>): string {
            var select2 = editor.element.data("select2");

            if (select2 != null) {
                var data = editor.element.select2("data");

                if (data == null) {
                    return "";
                }

                return Q.coalesce(data.text, "");
            }

            var value = getValue(editor);

            if (value == null) {
                return "";
            }

            if (typeof value === "string") {
                return value;
            }

            if (value instanceof Boolean) {
                return (!!value ? Q.coalesce(Q.tryGetText("Controls.FilterPanel.OperatorNames.true"), "True") :
                    Q.coalesce(Q.tryGetText("Controls.FilterPanel.OperatorNames.true"), "False"));
            }

            return value.toString();
        }

        var dummy: PropertyItem = {name: "_",};

        export function getValue(editor: Serenity.Widget<any>): any {
            var target = {};
            saveValue(editor, dummy, target);

            return target["_"];
        }

        export function saveValue(editor: Serenity.Widget<any>, item: PropertyItem, target: any): void {
            var getEditValue = (ss as any).safeCast(editor, Serenity.IGetEditValue);

            if (getEditValue != null) {
                getEditValue.getEditValue(item, target);

                return;
            }

            var stringValue = (ss as any).safeCast(editor, Serenity.IStringValue);

            if (stringValue != null) {
                target[item.name] = stringValue.get_value();

                return;
            }

            var booleanValue = (ss as any).safeCast(editor, Serenity.IBooleanValue);

            if (booleanValue != null) {
                target[item.name] = booleanValue.get_value();

                return;
            }

            var doubleValue = (ss as any).safeCast(editor, Serenity.IDoubleValue);

            if (doubleValue != null) {
                var value = doubleValue.get_value();
                target[item.name] = (isNaN(value) ? null : value);

                return;
            }

            if ((editor as any).getEditValue != null) {
                (editor as any).getEditValue(item, target);

                return;
            }

            if (editor.element.is(":input")) {
                target[item.name] = editor.element.val();

                return;
            }
        }

        export function setValue(editor: Serenity.Widget<any>, value: any): void {
            var source = {_: value};
            loadValue(editor, dummy, source);
        }

        export function loadValue(editor: Serenity.Widget<any>, item: PropertyItem, source: any): void {
            var setEditValue = (ss as any).safeCast(editor, Serenity.ISetEditValue);

            if (setEditValue != null) {
                setEditValue.setEditValue(source, item);

                return;
            }

            var stringValue = (ss as any).safeCast(editor, Serenity.IStringValue);

            if (stringValue != null) {
                var value = source[item.name];

                if (value != null) {
                    value = value.toString();
                }

                stringValue.set_value((ss as any).cast(value, String));

                return;
            }

            var booleanValue = (ss as any).safeCast(editor, Serenity.IBooleanValue);

            if (booleanValue != null) {
                var value1 = source[item.name];

                if (typeof value1 === "number") {
                    booleanValue.set_value(value1 > 0);
                } else {
                    booleanValue.set_value(!!value1);
                }

                return;
            }

            var doubleValue = (ss as any).safeCast(editor, Serenity.IDoubleValue);

            if (doubleValue != null) {
                var d = source[item.name];

                if (!!(d == null || (ss as any).isInstanceOfType(d, String) && Q.isTrimmedEmpty((ss as any).cast(d, String)))) {
                    doubleValue.set_value(null);
                } else if ((ss as any).isInstanceOfType(d, String)) {
                    doubleValue.set_value((ss as any).cast(Q.parseDecimal((ss as any).cast(d, String)), Number));
                } else if ((ss as any).isInstanceOfType(d, Boolean)) {
                    doubleValue.set_value((!!d ? 1 : 0));
                } else {
                    doubleValue.set_value((ss as any).cast(d, Number));
                }

                return;
            }

            if ((editor as any).setEditValue != null) {
                (editor as any).setEditValue(source, item);

                return;
            }

            if (editor.element.is(":input")) {
                var v = source[item.name];

                if (v == null) {
                    editor.element.val("");
                } else {
                    editor.element.val(v);
                }

                return;
            }
        }

        export function setReadonly(elements: JQuery, isReadOnly: boolean): JQuery {
            elements.each(function (index, el) {
                var elx = $(el);
                var type = elx.attr("type");

                if (elx.is("select") || type === "radio" || type === "checkbox") {
                    if (isReadOnly) {
                        elx.addClass("readonly").attr("disabled", "disabled");
                    } else {
                        elx.removeClass("readonly").removeAttr("disabled");
                    }
                } else if (isReadOnly) {
                    elx.addClass("readonly").attr("readonly", "readonly");
                } else {
                    elx.removeClass("readonly").removeAttr("readonly");
                }

                return true;
            });

            return elements;
        }

        export function setReadOnly(widget: Serenity.Widget<any>, isReadOnly: boolean): void {
            var readOnly = (ss as any).safeCast(widget, Serenity.IReadOnly);

            if (readOnly != null) {
                readOnly.set_readOnly(isReadOnly);
            } else if (widget.element.is(":input")) {
                setReadonly(widget.element, isReadOnly);
            }
        }

        export function setRequired(widget: Serenity.Widget<any>, isRequired: boolean): void {
            var req = (ss as any).safeCast(widget, IValidateRequired);

            if (req != null) {
                req.set_required(isRequired);
            } else if (widget.element.is(":input")) {
                widget.element.toggleClass("required", !!isRequired);
            }

            var gridField = WX.getGridField(widget),
                hasSupItem = gridField.find("sup").get().length;

            if (isRequired && !hasSupItem) {
                $("<sup>*</sup>").attr("title", Q.text("Controls.PropertyGrid.RequiredHint"))
                    .prependTo(gridField.find(".caption")[0]);
            } else if (!isRequired && hasSupItem) {
                $(gridField.find("sup")[0]).remove();
            }
        }

        export function setContainerReadOnly(container: JQuery, readOnly: boolean) {
            if (!readOnly) {
                if (!container.hasClass("readonly-container")) {
                    return;
                }

                container.removeClass("readonly-container").find(".editor.container-readonly")
                    .removeClass("container-readonly").each((i, e) => {
                    var w = $(e).tryGetWidget(Serenity.Widget);

                    if (w != null) {
                        Serenity.EditorUtils.setReadOnly(w, false);
                    } else {
                        Serenity.EditorUtils.setReadonly($(e), false);
                    }
                });

                return;
            }

            container.addClass("readonly-container").find(".editor")
                .not(".container-readonly")
                .each((i, e) => {
                    var w = $(e).tryGetWidget(Serenity.Widget);

                    if (w != null) {
                        if (w["get_readOnly"]) {
                            if (w["get_readOnly"]()) {
                                return;
                            }
                        } else if ($(e).is("[readonly]") || $(e).is("[disabled]") || $(e).is(".readonly") || $(e).is(".disabled")) {
                            return;
                        }

                        $(e).addClass("container-readonly");
                        Serenity.EditorUtils.setReadOnly(w, true);
                    } else {
                        if ($(e).is("[readonly]") || $(e).is("[disabled]") || $(e).is(".readonly") || $(e).is(".disabled")) {
                            return;
                        }

                        Serenity.EditorUtils.setReadonly($(e).addClass("container-readonly"), true);
                    }
                });
        }
    }

    function Editor(name: string, intf?: any[]) {
        return Decorators.registerEditor(`Serenity.${name}Editor`, intf);
    }

    @Editor("Boolean", [IBooleanValue])
    @Element('<input type="checkbox" />')
    export class BooleanEditor extends Widget<any> {
        constructor(input: JQuery) {
            super(input);

            input.removeClass("flexify");
        }

        public get value(): boolean {
            return this.element.is(":checked");
        }

        public set value(value: boolean) {
            this.element.prop("checked", !!value);
        }

        protected get_value(): boolean {
            return this.value;
        }

        protected set_value(value: boolean): void {
            this.value = value;
        }
    }

    @Editor("Decimal", [IDoubleValue])
    @Element('<input type="text" />')
    export class DecimalEditor extends Widget<DecimalEditorOptions> implements IDoubleValue {
        constructor(input: JQuery, opt?: DecimalEditorOptions) {
            super(input, opt);

            input.addClass("decimalQ");
            var numericOptions = $.extend(Serenity.DecimalEditor.defaultAutoNumericOptions(), {
                vMin: Q.coalesce(this.options.minValue, (this.options.allowNegatives) ? (this.options.maxValue != null ? (`-${this.options.maxValue}`) : "-999999999999.99") : "0.00"),
                vMax: Q.coalesce(this.options.maxValue, "999999999999.99"),
            });

            if (this.options.decimals != null) {
                numericOptions.mDec = this.options.decimals;
            }

            if (this.options.padDecimals != null) {
                numericOptions.aPad = this.options.padDecimals;
            }

            (input as any).autoNumeric(numericOptions);
        }

        get value(): number {
            return this.get_value();
        }

        set value(v: number) {
            this.set_value(v);
        }

        static defaultAutoNumericOptions(): any {
            return {
                aSep: ",",//((Q.Culture.decimalSeparator === ".") ? "," : "."), // Thousand separator character
                aDec: Q.Culture.decimalSeparator, // Decimal character
                altDec: ",",//((Q.Culture.decimalSeparator === ".") ? "," : "."), // Alt decimal character
                aPad: true,
            };
        }

        get_value(): number {
            var val = (this.element as any).autoNumeric("get");

            if (!!(val == null || val === "")) {
                return null;
            }

            return parseFloat(val);
        }

        set_value(value: number) {
            if (value == null || (value as any) === "") {
                this.element.val("");
            } else {
                (this.element as any).autoNumeric("set", value);
            }
        }

        get_isValid(): boolean {
            return !isNaN(this.get_value());
        }
    }

    export interface IntegerEditorOptions {
        minValue?: number;
        maxValue?: number;
        allowNegatives?: boolean;
    }

    @Editor("Integer", [IDoubleValue])
    @Element('<input type="text" />')
    export class IntegerEditor extends Widget<IntegerEditorOptions> implements IDoubleValue {
        constructor(input: JQuery, opt?: IntegerEditorOptions) {
            super(input, opt);

            input.addClass("integerQ");
            var numericOptions = $.extend(Serenity.DecimalEditor.defaultAutoNumericOptions(),
                {
                    vMin: Q.coalesce(this.options.minValue, this.options.allowNegatives ? (this.options.maxValue != null ? ("-" + this.options.maxValue) : '-2147483647') : '0'),
                    vMax: Q.coalesce(this.options.maxValue, 2147483647),
                    //aSep: null,
                });

            (input as any).autoNumeric(numericOptions);
        }

        get value(): number {
            return this.get_value();
        }

        set value(v: number) {
            this.set_value(v);
        }

        get_value(): number {
            var val = (this.element as any).autoNumeric("get");

            if (!!Q.isTrimmedEmpty(val)) {
                return null;
            } else {
                return parseInt(val, 10);
            }
        }

        set_value(value: number) {
            if (value == null || (value as any) === "") {
                this.element.val("");
            } else {
                (this.element as any).autoNumeric("set", value);
            }
        }

        get_isValid(): boolean {
            return !isNaN(this.get_value());
        }
    }

    export interface DecimalEditorOptions {
        minValue?: string;
        maxValue?: string;
        decimals?: any;
        padDecimals?: any;
        allowNegatives?: boolean;
    }

    export interface EmailEditorOptions {
        domain?: string;
        readOnlyDomain?: boolean;
    }

    @Editor("Email", [IStringValue, IReadOnly])
    @Element('<input type="text" />')
    export class EmailEditor extends Widget<EmailEditorOptions> {
        constructor(input: JQuery, opt: EmailEditorOptions) {
            super(input, opt);

            EmailEditor.registerValidationMethods();
            input.addClass("emailuser").removeClass("flexify");
            var spanAt = $("<span />").text("@").addClass("emailat").insertAfter(input);
            var domain = $('<input type="text" />').addClass("emaildomain").addClass("flexify").insertAfter(spanAt);
            domain.bind(`blur.${this.uniqueName}`, function () {
                var validator = domain.closest("form").data("validator");

                if (validator != null) {
                    validator.element(input[0]);
                }
            });

            if (!Q.isEmptyOrNull(this.options.domain)) {
                domain.val(this.options.domain);
            }

            if (this.options.readOnlyDomain) {
                domain.attr("readonly", "readonly").addClass("disabled").attr("tabindex", "-1");
            }

            input.bind(`keypress.${this.uniqueName}`, e => {
                if (e.which === 64) {
                    e.preventDefault();

                    if (!this.options.readOnlyDomain) {
                        domain.focus();
                        domain.select();
                    }
                }
            });

            domain.bind(`keypress.${this.uniqueName}`, function (e1) {
                if (e1.which === 64) {
                    e1.preventDefault();
                }
            });

            if (!this.options.readOnlyDomain) {
                input.change(e2 => this.set_value(input.val()));
            }
        }

        get value(): string {
            return this.get_value();
        }

        set value(v: string) {
            this.set_value(v);
        }

        static registerValidationMethods(): void {
            if ($.validator.methods["emailuser"] != null) {
                return;
            }

            $.validator.addMethod("emailuser", function (value, element) {
                var domain = $(element).nextAll(".emaildomain");

                if (domain.length && domain.attr("readonly") == null) {
                    if (this.optional(element) && this.optional(domain[0])) {
                        return true;
                    }

                    value = `${value}@${domain.val()}`;

                    if (Q.Config.emailAllowOnlyAscii) {
                        return (new RegExp("^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}" +
                            "[a-zA-Z0-9])?(?:\\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$"))
                            .test(value);
                    }

                    return (new RegExp("^((([a-z]|\\d|[!#\\$%&'\\*\\+\\-\\/=\\?\\^_`{\\|}~]|" +
                        "[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])+(\\.([a-z]|\\d|" +
                        "[!#\\$%&'\\*\\+\\-\\/=\\?\\^_`{\\|}~]|[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])+)*)|" +
                        "((\\x22)((((\\x20|\\x09)*(\\x0d\\x0a))?(\\x20|\\x09)+)?(([\\x01-\\x08\\x0b\\x0c\\x0e-\\x1f\\x7f]|" +
                        "\\x21|[\\x23-\\x5b]|[\\x5d-\\x7e]|[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])|(\\\\([\\x01-\\x09\\x0b\\x0c\\x0d-\\x7f]|" +
                        "[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF]))))*(((\\x20|\\x09)*(\\x0d\\x0a))?(\\x20|\\x09)+)?(\\x22)))@((([a-z]|\\d|" +
                        "[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])|(([a-z]|\\d|[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])" +
                        "([a-z]|\\d|-|\\.|_|~|[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])*([a-z]|\\d|[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])))\\.)" +
                        "+(([a-z]|[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])|(([a-z]|[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])([a-z]|\\d|-|\\.|_|~|" +
                        "[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])*([a-z]|[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])))$", 'i')).test(value);
                } else {
                    if (Q.Config.emailAllowOnlyAscii) {
                        return this.optional(element) || (new RegExp("^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+$")).test(value);
                    }

                    return this.optional(element) || (new RegExp("^((([a-z]|\\d|[!#\\$%&'\\*\\+\\-\\/=\\?\\^_`{\\|}~]|[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])+" +
                        "(\\.([a-z]|\\d|[!#\\$%&'\\*\\+\\-\\/=\\?\\^_`{\\|}~]|[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])+)*)|((\\x22)((((\\x20|\\x09)*" +
                        "(\\x0d\\x0a))?(\\x20|\\x09)+)?(([\\x01-\\x08\\x0b\\x0c\\x0e-\\x1f\\x7f]|\\x21|[\\x23-\\x5b]|[\\x5d-\\x7e]|[\\u00A0-\\uD7FF\\uF900-" +
                        "\\uFDCF\\uFDF0-\\uFFEF])|(\\\\([\\x01-\\x09\\x0b\\x0c\\x0d-\\x7f]|[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF]))))*(((\\x20|\\x09)*" +
                        "(\\x0d\\x0a))?(\\x20|\\x09)+)?(\\x22)))$", 'i')).test(value);
                }
            }, Q.text("Validation.Email"));
        }

        get_value(): string {
            var domain = this.element.nextAll(".emaildomain"),
                value = this.element.val(),
                domainValue = domain.val();

            if (Q.isEmptyOrNull(value)) {
                if (this.options.readOnlyDomain || Q.isEmptyOrNull(domainValue)) {
                    return "";
                }

                return `@${domainValue}`;
            }

            return `${value}@${domainValue}`;
        }

        set_value(value: string): void {
            var domain = this.element.nextAll(".emaildomain");
            value = Q.trimToNull(value);

            if (value == null) {
                if (!this.options.readOnlyDomain) {
                    domain.val("");
                }

                this.element.val("");
            } else {
                var parts = value.split("@");

                if (parts.length > 1) {
                    if (!this.options.readOnlyDomain) {
                        domain.val(parts[1]);
                        this.element.val(parts[0]);
                    } else if (!Q.isEmptyOrNull(this.options.domain)) {
                        if (parts[1] !== this.options.domain) {
                            this.element.val(value);
                        } else {
                            this.element.val(parts[0]);
                        }
                    } else {
                        this.element.val(parts[0]);
                    }
                } else {
                    this.element.val(parts[0]);
                }
            }
        }

        get_readOnly(): boolean {
            var domain = this.element.nextAll(".emaildomain");

            return !(this.element.attr("readonly") == null &&
                (!this.options.readOnlyDomain || domain.attr("readonly") == null));
        }

        set_readOnly(value: boolean): void {
            var domain = this.element.nextAll(".emaildomain");

            if (value) {
                this.element.attr("readonly", "readonly").addClass("readonly");

                if (!this.options.readOnlyDomain) {
                    domain.attr("readonly", "readonly").addClass("readonly");
                }
            } else {
                this.element.removeAttr("readonly").removeClass("readonly");

                if (!this.options.readOnlyDomain) {
                    domain.removeAttr("readonly").removeClass("readonly");
                }
            }
        }
    }

    export interface EnumEditorOptions extends Select2CommonOptions {
        enumKey?: string;
        enumType?: any;
    }

    @Editor("Enum")
    export class EnumEditor extends Select2Editor<EnumEditorOptions, Select2Item> {
        constructor(hidden: JQuery, opt: EnumEditorOptions) {
            super(hidden, opt);
            this.updateItems();
        }

        protected updateItems(): void {
            this.clearItems();

            var enumType = this.options.enumType || Serenity.EnumTypeRegistry.get(this.options.enumKey);
            var enumKey = this.options.enumKey;

            if (enumKey == null && enumType != null) {
                var enumKeyAttr = (ss as any).getAttributes(enumType, Serenity.EnumKeyAttribute, false);
                if (enumKeyAttr.length > 0) {
                    enumKey = enumKeyAttr[0].value;
                }
            }

            var values = (ss as any).Enum.getValues(enumType);
            for (var x of values) {
                var name = (ss as any).Enum.toString(enumType, x);
                this.addOption((ss as any).cast(x, (ss as any).Int32).toString(),
                    Q.coalesce(Q.tryGetText('Enums.' + enumKey + '.' + name), name), null, false);
            }
        }

        protected allowClear() {
            return Q.coalesce(this.options.allowClear, true);
        }
    }

    export interface HtmlContentEditorOptions {
        cols?: any;
        rows?: any;
    }

    @Editor("HtmlContent", [IStringValue, IReadOnly])
    @Element("<textarea />")
    export class HtmlContentEditor extends Widget<HtmlContentEditorOptions> implements IStringValue, IReadOnly {
        static CKEditorVer = "4.7.1";
        private _instanceReady: boolean;

        constructor(textArea: JQuery, opt?: HtmlContentEditorOptions) {
            super(textArea, opt);

            this._instanceReady = false;
            HtmlContentEditor.includeCKEditor();

            var id = textArea.attr("id");

            if (Q.isTrimmedEmpty(id)) {
                textArea.attr("id", this.uniqueName);
                id = this.uniqueName;
            }

            if (this.options.cols != null) {
                textArea.attr("cols", this.options.cols);
            }

            if (this.options.rows != null) {
                textArea.attr("rows", this.options.rows);
            }

            this.addValidationRule(this.uniqueName, e => {
                if (e.hasClass("required")) {
                    var value = Q.trimToNull(this.get_value());

                    if (value == null) {
                        return Q.text("Validation.Required");
                    }
                }

                return null;
            });

            Serenity.LazyLoadHelper.executeOnceWhenShown(this.element, () => {
                var config = this.getConfig();
                window["CKEDITOR"] && window["CKEDITOR"].replace(id, config);
            });
        }

        get value(): string {
            return this.get_value();
        }

        set value(v: string) {
            this.set_value(v);
        }

        static includeCKEditor(): void {
            if (window["CKEDITOR"]) {
                return;
            }

            var script = $("#CKEditorScript");

            if (script.length) {
                return;
            }

            $("<script />").attr("id", "CKEditorScript")
                .attr("src", Q.resolveUrl(`~/Scripts/CKEditor/ckeditor.js?v=${HtmlContentEditor.CKEditorVer}`))
                .appendTo(window.document.head);
        };

        destroy(): void {
            var instance = this.getEditorInstance();
            instance && instance.destroy(true);
            super.destroy();
        }

        get_value(): string {
            var instance = this.getEditorInstance();

            if (this._instanceReady && instance) {
                return instance.getData();
            } else {
                return this.element.val();
            }
        }

        set_value(value: string): void {
            var instance = this.getEditorInstance();
            this.element.val(value);

            if (this._instanceReady && instance) {
                instance.setData(value);
            }
        }

        get_readOnly(): boolean {
            return !Q.isEmptyOrNull(this.element.attr("disabled"));
        }

        set_readOnly(value: boolean) {
            if (this.get_readOnly() !== value) {
                if (value) {
                    this.element.attr("disabled", "disabled");
                } else {
                    this.element.removeAttr("disabled");
                }

                var instance = this.getEditorInstance();

                if (this._instanceReady && instance) {
                    instance.setReadOnly(value);
                }
            }
        }

        protected instanceReady(x: any): void {
            this._instanceReady = true;
            $(x.editor.container.$).addClass(this.element.attr("class"));
            this.element.addClass("select2-offscreen").css("display", "block");

            // for validation to work
            x.editor.setData(this.element.val());
            x.editor.setReadOnly(this.get_readOnly());
        }

        protected getLanguage(): string {
            if (!window["CKEDITOR"]) {
                return "en";
            }

            var CKEDITOR = window["CKEDITOR"],
                lang = Q.trimToNull($("html").attr("lang")) || "en";

            if (!!CKEDITOR.lang.languages[lang]) {
                return lang;
            }

            if (lang.indexOf(String.fromCharCode(45)) >= 0) {
                lang = lang.split(String.fromCharCode(45))[0];
            }

            if (!!CKEDITOR.lang.languages[lang]) {
                return lang;
            }

            return "en";
        }

        protected getConfig(): CKEditorConfig {
            return {
                customConfig: "",
                language: this.getLanguage(),
                bodyClass: "s-HtmlContentBody",
                on: {
                    instanceReady: (x: any) => this.instanceReady(x),
                    change: (x1: any) => {
                        x1.editor.updateElement();
                        this.element.triggerHandler("change");
                    },
                },
                toolbarGroups: [
                    {
                        name: 'clipboard',
                        groups: ['clipboard', 'undo']
                    }, {
                        name: 'editing',
                        groups: ['find', 'selection', 'spellchecker']
                    }, {
                        name: 'insert',
                        groups: ['links', 'insert', 'blocks', 'bidi', 'list', 'indent']
                    }, {
                        name: 'forms',
                        groups: ['forms', 'mode', 'document', 'doctools', 'others', 'about', 'tools']
                    }, {
                        name: 'colors'
                    }, {
                        name: 'basicstyles',
                        groups: ['basicstyles', 'cleanup']
                    }, {
                        name: 'align'
                    }, {
                        name: 'styles'
                    }],
                removeButtons: 'SpecialChar,Anchor,Subscript,Styles',
                format_tags: 'p;h1;h2;h3;pre',
                removeDialogTabs: 'image:advanced;link:advanced',
                removePlugins: 'uploadimage,image2',
                contentsCss: Q.resolveUrl('~/Content/site/site.htmlcontent.css'),
                entities: false,
                entities_latin: false,
                entities_greek: false,
                autoUpdateElement: true,
                height: (this.options.rows == null || this.options.rows === 0) ? null :
                    (`${this.options.rows * 20}px`)
            };
        }

        protected getEditorInstance() {
            var id = this.element.attr("id");

            return window["CKEDITOR"].instances[id];
        }
    }

    @Editor("HtmlNoteContent")
    export class HtmlNoteContentEditor extends HtmlContentEditor {
        constructor(textArea: JQuery, opt?: HtmlContentEditorOptions) {
            super(textArea, opt);
        }

        protected getConfig(): CKEditorConfig {
            var config = super.getConfig();
            (config as any).removeButtons += ',Cut,Copy,Paste,BulletedList,NumberedList,' +
                'Indent,Outdent,SpecialChar,Subscript,Superscript,Styles,PasteText,' +
                'PasteFromWord,Strike,Link,Unlink,CreatePlaceholder,Image,Table,' +
                'HorizontalRule,Source,Maximize,Format,Font,FontSize,Anchor,Blockquote,' +
                'CreatePlaceholder,BGColor,JustifyLeft,JustifyCenter,' +
                'JustifyRight,JustifyBlock,Superscript,RemoveFormat';

            (config as any).removePlugins = "elementspath,uploadimage,image2";

            return config;
        }
    }

    @Editor("HtmlReportContent")
    export class HtmlReportContentEditor extends HtmlContentEditor {
        constructor(textArea: JQuery, opt?: HtmlContentEditorOptions) {
            super(textArea, opt);
        }

        protected getConfig(): CKEditorConfig {
            var config = super.getConfig();
            (config as any).removeButtons += ',Cut,Copy,Paste,BulletedList,NumberedList,' +
                'Indent,Outdent,SpecialChar,Subscript,Superscript,Styles,' +
                'PasteText,PasteFromWord,Strike,Link,Unlink,CreatePlaceholder,' +
                'Image,Table,HorizontalRule,Source,Maximize,Format,Font,FontSize,' +
                'Anchor,Blockquote,CreatePlaceholder,BGColor,JustifyLeft,JustifyCenter,' +
                'JustifyRight,JustifyBlock,Superscript,RemoveFormat';

            (config as any).removePlugins = "elementspath,uploadimage,image2";

            return config;
        }
    }

    export interface MapOptions {
        latitude: string;
        longitude: string;
        height: number | string,
        minZoom?: number;
        zoom?: number;
        maxZoom?: number;
        marker?: boolean;
        editable?: boolean;
    }

    export interface Location {
        lat: number;
        lng: number;
    }

    @Editor("Map", [])
    @Element("<div />")
    export class MapEditor extends Widget<MapOptions> implements IGetEditValue, ISetEditValue {
        private readonly map: any;
        private marker: any;
        protected entity: Location;

        constructor(container: JQuery, opt: MapOptions) {
            super(container, opt);

            // MapEditor.loadLibrary();

            container.height((isNaN(+this.options.height) ? this.options.height : +this.options.height) || 200);

            const center: Location = L.latLng(+(Q.trimToNull(this.options.latitude) || 0), +(Q.trimToNull(this.options.longitude) || 0));

            this.map = L.map(container[0], {
                center,
                minZoom: +(this.options.minZoom || 3),
                zoom: +(this.options.zoom || 3),
                maxZoom: +(this.options.maxZoom || 20),
                // layers: [L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
                layers: [L.tileLayer("http://tile-{s}.openstreetmap.fr/hot/{z}/{x}/{y}.png", {
                    attribution: '&copy; <a href="http://vision-it.ir/" title="Vision IT" target="_blank" style="cursor: pointer;">Vision IT</a>',
                }),],
            }).on("resize", function (event: any) {
                this.setView(center);
            });

            Serenity.LazyLoadHelper.executeOnceWhenShown(container, () => {
                this.map.invalidateSize(true);
                this.map.setView(center);
            });

            if (this.options.marker) {
                this.set_value(center);
            }
        }

        // static loadLibrary() {
        //     if ()
        // }

        get value(): Location {
            return this.get_value();
        }

        set value(v: Location) {
            this.set_value(v);
        }

        get_value(): Location {
            if (!this.entity) {
                return null;
            }

            let output: any = {};

            return $.extend(output, this.entity);
        }

        set_value(value: Location): void {
            if (!value || (typeof value !== "object")) {
                this.entity = null;

                this.removeMarker();
            }

            this.entity = value;
            this.setMarker(value);
        }

        getEditValue(property: PropertyItem, target: any) {
            target[property.name] = JSON.stringify(this.get_value());
        }

        setEditValue(source: any, property: Serenity.PropertyItem): void {
            const value = source[property.name];

            if (!value) {
                return;
            }

            let location: Location;

            try {
                location = JSON.parse(value);
            } catch (error) {
                return;
            }

            this.set_value(location);
        }

        protected setMarker(location: Location): void {
            if (!this.options.marker) {
                return;
            }

            if (this.marker) {
                this.marker.setLatLng(location);

                return;
            }

            this.marker = L.marker(location).addTo(this.map);

            if (+(this.options.editable || true)) {
                const map = this;
                this.map.on("click", function (event: any) {
                    const location: Location = event.latlng;

                    map.set_value(location);
                });
            }
        }

        protected removeMarker(): boolean {
            if (!this.options.marker || !this.marker) {
                return false;
            }

            this.marker.remove();

            return true;
        }
    }

    export interface ImageUploadEditorOptions {
        minWidth?: number;
        maxWidth?: number;
        minHeight?: number;
        maxHeight?: number;
        minSize?: number;
        maxSize?: number;
        originalNameProperty?: string;
        urlPrefix?: string;
        displayFileName?: boolean;
    }

    export abstract class UploadEditor extends Widget<ImageUploadEditorOptions> {
        static getToolButtons(): ToolButton[] {
            return [{
                title: Q.text("Controls.ImageUpload.AddFileButton"),
                cssClass: "add-file-button",
                onClick: () => {
                },
            },];
        }

        static updateInterface(fileSymbols: any, toolbar: any, state: boolean): void {
            const $addButton = toolbar.findButton("add-file-button");
            $addButton.toggleClass("disabled", state);
            fileSymbols.find("button.delete-button").toggle(!state);
        }
    }

    @Editor("ImageUpload", [IReadOnly])
    @Element("<div />")
    export class ImageUploadEditor extends UploadEditor implements IReadOnly, IGetEditValue, ISetEditValue {
        private entity: UploadedFile;
        protected toolbar: Toolbar;
        protected progress: JQuery;
        protected fileSymbols: JQuery;
        private uploadInput: JQuery;

        constructor(div: JQuery, opt: ImageUploadEditorOptions) {
            super(div, opt);

            if (Q.isEmptyOrNull(this.options.originalNameProperty)) {
                div.addClass("hide-original-name");
            }

            this.toolbar = new Toolbar($("<div />").appendTo(this.element), {
                buttons: UploadEditor.getToolButtons(),
            });
            this.progress = $("<div><div /></div>").addClass("upload-progress").prependTo(this.toolbar.element);

            this.uploadInput = UploadHelper.addUploadInput({
                container: this.toolbar.findButton("add-file-button"),
                inputName: this.uniqueName,
                zone: this.element,
                progress: this.progress,
                allowMultiple: false,
                validation: {
                    minSize: opt.minSize,
                    maxSize: opt.maxSize,
                },
                fileDone: (response, name, data) => {
                    this.entity = {
                        OriginalName: name,
                        Filename: response.TemporaryFile,
                    };
                    this.populate();
                    UploadEditor.updateInterface(this.fileSymbols, this.toolbar, this.get_readOnly());
                },
            });

            this.fileSymbols = $("<ul />").appendTo(this.element);
            UploadEditor.updateInterface(this.fileSymbols, this.toolbar, this.get_readOnly());
        }

        get value(): UploadedFile {
            return this.get_value();
        }

        set value(v: UploadedFile) {
            this.set_value(v);
        }

        get_readOnly(): boolean {
            return this.uploadInput.attr("disabled") != null;
        }

        set_readOnly(value: boolean): void {
            if (this.get_readOnly() === value) {
                return;
            }

            if (value) {
                (this.uploadInput.attr("disabled", "disabled") as any).fileupload("disable");
            } else {
                (this.uploadInput.removeAttr("disabled") as any).fileupload("enable");
            }

            UploadEditor.updateInterface(this.fileSymbols, this.toolbar, this.get_readOnly());
        }

        get_value(): UploadedFile {
            if (this.entity == null) {
                return null;
            }

            return $.extend({}, this.entity);
        }

        set_value(value: UploadedFile): void {
            if (value != null) {
                if (value.Filename == null) {
                    this.entity = null;
                } else {
                    this.entity = $.extend({}, value);
                }
            } else {
                this.entity = null;
            }

            this.populate();
            UploadEditor.updateInterface(this.fileSymbols, this.toolbar, this.get_readOnly());
        }

        getEditValue(property: PropertyItem, target: any) {
            target[property.name] = (this.entity == null) ? null : Q.trimToNull(this.entity.Filename);
        }

        setEditValue(source: any, property: PropertyItem) {
            const value: UploadedFile = {};
            value.Filename = source[property.name];

            if (Q.isEmptyOrNull(this.options.originalNameProperty)) {
                if (this.options.displayFileName) {
                    const s = Q.coalesce(value.Filename, ""),
                        index = (ss as any).lastIndexOfAnyString(s, [47, 92]);

                    if (index >= 0) {
                        value.OriginalName = s.substr(index + 1);
                    } else {
                        value.OriginalName = s;
                    }
                }
            } else {
                value.OriginalName = source[this.options.originalNameProperty];
            }

            this.set_value(value);
        }

        protected populate(): void {
            const displayOriginalName = this.options.displayFileName || !Q.isTrimmedEmpty(this.options.originalNameProperty);
            UploadHelper.populateFileSymbols(this.fileSymbols, (this.entity == null) ? null : [this.entity], displayOriginalName, this.options.urlPrefix, this);
        }
    }

    @Editor("MultipleImageUpload", [IReadOnly])
    @Element("<div />")
    export class MultipleImageUploadEditor extends UploadEditor implements IReadOnly, IGetEditValue, ISetEditValue {
        private entities: UploadedFile[] = [];
        @Option()
        public jsonEncodeValue: boolean;
        protected toolbar: Toolbar;
        protected progress: JQuery;
        protected fileSymbols: JQuery;
        private uploadInput: JQuery;

        constructor(div: JQuery, opt: ImageUploadEditorOptions) {
            super(div, opt);

            this.toolbar = new Toolbar($("<div/>").appendTo(this.element), {
                buttons: UploadEditor.getToolButtons(),
            });
            this.progress = $("<div><div /></div>").addClass("upload-progress").prependTo(this.toolbar.element);
            this.uploadInput = Serenity.UploadHelper.addUploadInput({
                container: this.toolbar.findButton("add-file-button"),
                inputName: this.uniqueName,
                zone: this.element,
                progress: this.progress,
                allowMultiple: true,
                validation: {
                    minSize: opt.minSize,
                    maxSize: opt.maxSize,
                },
                fileDone: (response, name, data) => {
                    this.entities.push({
                        OriginalName: name,
                        Filename: response.TemporaryFile,
                    });
                    this.populate();
                    UploadEditor.updateInterface(this.fileSymbols, this.toolbar, this.get_readOnly());
                },
            });

            this.fileSymbols = $("<ul />").appendTo(this.element);
            UploadEditor.updateInterface(this.fileSymbols, this.toolbar, this.get_readOnly());
        }

        get value(): UploadedFile[] {
            return this.get_value();
        }

        set value(v: UploadedFile[]) {
            this.set_value(v);
        }

        get_readOnly(): boolean {
            return this.uploadInput.attr("disabled") != null;
        }

        set_readOnly(value: boolean): void {
            if (this.get_readOnly() === value) {
                return;
            }

            if (value) {
                (this.uploadInput.attr("disabled", "disabled") as any).fileupload("disable");
            } else {
                (this.uploadInput.removeAttr("disabled") as any).fileupload("enable");
            }

            UploadEditor.updateInterface(this.fileSymbols, this.toolbar, this.get_readOnly());
        }

        get_value(): UploadedFile[] {
            return this.entities.map(function (x) {
                return $.extend({}, x);
            });
        }

        set_value(value: UploadedFile[]) {
            this.entities = (value || []).map(function (x) {
                return $.extend({}, x);
            });
            this.populate();
            UploadEditor.updateInterface(this.fileSymbols, this.toolbar, this.get_readOnly());
        }

        getEditValue(property: PropertyItem, target: any) {
            if (this.jsonEncodeValue) {
                target[property.name] = JSON.stringify(this.get_value());
            } else {
                target[property.name] = this.get_value();
            }
        }

        setEditValue(source: any, property: PropertyItem) {
            const val = source[property.name];

            if ((ss as any).isInstanceOfType(val, String)) {
                const json = Q.coalesce(Q.trimToNull(val), "[]");

                if (Q.startsWith(json, "[") && Q.endsWith(json, "]")) {
                    this.set_value(JSON.parse(json));
                } else {
                    this.set_value([{
                        Filename: json,
                        OriginalName: "UnknownFile",
                    }]);
                }
            } else {
                this.set_value(val as any);
            }
        }

        protected populate(): void {
            UploadHelper.populateFileSymbols(this.fileSymbols, this.entities, true, this.options.urlPrefix, this);
        }
    }

    // http://digitalbush.com/projects/masked-input-plugin/
    @Editor("Masked", [IStringValue])
    @Element('<input type="text" />')
    export class MaskedEditor extends Widget<MaskedEditorOptions> {
        constructor(input: JQuery, opt?: MaskedEditorOptions) {
            super(input, opt);

            (input as any).mask(this.options.mask || "", {
                placeholder: Q.coalesce(this.options.placeholder, "_"),
            });
        }

        public get value(): string {
            this.element.triggerHandler("blur.mask");
            return this.element.val();
        }

        public set value(value: string) {
            this.element.val(value);
        }

        protected get_value(): string {
            return this.value;
        }

        protected set_value(value: string): void {
            this.value = value;
        }
    }

    export interface MaskedEditorOptions {
        mask?: string;
        placeholder?: string;
    }

    @Editor("String", [IStringValue])
    @Element('<input type="text" />')
    export class StringEditor extends Widget<any> {
        constructor(input: JQuery) {
            super(input);
        }

        public get value(): string {
            return this.element.val();
        }

        public set value(value: string) {
            this.element.val(value);
        }

        protected get_value(): string {
            return this.value;
        }

        protected set_value(value: string): void {
            this.value = value;
        }
    }

    @Editor("EmailAddress")
    export class EmailAddressEditor extends Serenity.StringEditor {
        constructor(input: JQuery) {
            super(input);

            input.attr('type', 'email')
                .addClass('email');
        }
    }

    @Editor("Password")
    export class PasswordEditor extends StringEditor {
        constructor(input: JQuery) {
            super(input);

            input.attr('type', 'password');
        }
    }

    export interface RadioButtonEditorOptions {
        enumKey?: string;
        enumType?: any;
        lookupKey?: string;
    }

    @Editor("RadioButton", [IStringValue, IReadOnly])
    @Element("<div />")
    export class RadioButtonEditor extends Widget<RadioButtonEditorOptions> implements IReadOnly {
        constructor(input: JQuery, opt: RadioButtonEditorOptions) {
            super(input, opt);

            if (Q.isEmptyOrNull(this.options.enumKey) &&
                this.options.enumType == null &&
                Q.isEmptyOrNull(this.options.lookupKey)) {
                return;
            }

            if (!Q.isEmptyOrNull(this.options.lookupKey)) {
                var lookup = Q.getLookup(this.options.lookupKey);

                for (var item of lookup.items) {
                    var textValue = item[lookup.textField];
                    var text = (textValue == null ? '' : textValue.toString());
                    var idValue = item[lookup.idField];
                    var id = (idValue == null ? '' : idValue.toString());
                    this.addRadio(id, text);
                }
            } else {
                var enumType = this.options.enumType || Serenity.EnumTypeRegistry.get(this.options.enumKey);
                var enumKey = this.options.enumKey;

                if (enumKey == null && enumType != null) {
                    var enumKeyAttr = (ss as any).getAttributes(enumType, Serenity.EnumKeyAttribute, false);

                    if (enumKeyAttr.length > 0) {
                        enumKey = enumKeyAttr[0].value;
                    }
                }

                var values = (ss as any).Enum.getValues(enumType);

                for (var x of values) {
                    var name = (ss as any).Enum.toString(enumType, x);
                    this.addRadio(x.toString(), Q.coalesce(Q.tryGetText(
                        'Enums.' + enumKey + '.' + name), name));
                }
            }
        }

        get value(): string {
            return this.get_value();
        }

        set value(v: string) {
            this.set_value(v);
        }

        get_value(): string {
            return this.element.find('input:checked').first().val();
        }

        set_value(value: string): void {
            if (value !== this.get_value()) {
                var inputs = this.element.find('input');
                var checks = inputs.filter(':checked');

                if (checks.length > 0) {
                    (checks[0] as HTMLInputElement).checked = false;
                }

                if (!Q.isEmptyOrNull(value)) {
                    checks = inputs.filter('[value=' + value + ']');

                    if (checks.length > 0) {
                        (checks[0] as HTMLInputElement).checked = true;
                    }
                }
            }
        }

        get_readOnly(): boolean {
            return this.element.attr('disabled') != null;
        }

        set_readOnly(value: boolean): void {
            if (this.get_readOnly() !== value) {
                if (value) {
                    this.element.attr('disabled', 'disabled')
                        .find('input').attr('disabled', 'disabled');
                } else {
                    this.element.removeAttr('disabled')
                        .find('input').removeAttr('disabled');
                }
            }
        }

        protected addRadio(value: string, text: string) {
            var label = $('<label/>').text(text);
            $('<input type="radio"/>').attr('name', this.uniqueName)
                .attr('id', this.uniqueName + '_' + value)
                .attr('value', value).prependTo(label);
            label.appendTo(this.element);
        }
    }

    export interface RecaptchaOptions {
        siteKey?: string;
        language?: string;
    }

    @Decorators.registerEditor("Serenity.Recaptcha", [IStringValue])
    @Decorators.element("<div />")
    export class Recaptcha extends Widget<RecaptchaOptions> implements IStringValue {
        constructor(div: JQuery, opt: RecaptchaOptions) {
            super(div, opt);

            this.element.addClass("g-recaptcha").attr("data-sitekey", this.options.siteKey);
            if (!!(window["grecaptcha"] == null && $("script#RecaptchaInclude").length === 0)) {
                var src = "https://www.google.com/recaptcha/api.js";
                var lng = this.options.language;

                if (lng == null) {
                    lng = Q.coalesce($('html').attr('lang'), '');
                }

                src += '?hl=' + lng;
                $('<script/>').attr('id', 'RecaptchaInclude').attr('src', src).appendTo(document.body);
            }

            var valInput = $('<input />').insertBefore(this.element)
                .attr('id', this.uniqueName + '_validate').val('x');

            var gro = {};
            gro["visibility"] = "hidden";
            gro["width"] = "0px";
            gro["height"] = "0px";
            gro["padding"] = "0px";

            var input = valInput.css(gro);
            var self = this;

            VX.addValidationRule(input, this.uniqueName, e => {
                if (Q.isEmptyOrNull(this.get_value())) {
                    return Q.text("Validation.Required");
                }

                return null;
            });
        }

        get_value(): string {
            return this.element.find(".g-recaptcha-response").val();
        }

        set_value(value: string): void {
        }
    }

    export interface TextAreaEditorOptions {
        cols?: number;
        rows?: number;
    }

    @Editor("TextArea", [IStringValue])
    @Element("<textarea />")
    export class TextAreaEditor extends Widget<TextAreaEditorOptions> {
        constructor(input: JQuery, opt?: TextAreaEditorOptions) {
            super(input, opt);

            if (this.options.cols !== 0) {
                input.attr("cols", Q.coalesce(this.options.cols, 80));
            }

            if (this.options.rows !== 0) {
                input.attr("rows", Q.coalesce(this.options.rows, 6));
            }
        }

        public get value(): string {
            return this.element.val();
        }

        public set value(value: string) {
            this.element.val(value);
        }

        protected get_value(): string {
            return this.value;
        }

        protected set_value(value: string): void {
            this.value = value;
        }
    }

    export interface TimeEditorOptions {
        minTime?: string;
        maxTime?: string;
        // intervalMinutes?: number;
    }

    @Editor("Time", [IDoubleValue,])
    @Element('<input type="time" />')
    export class TimeEditor extends Widget<TimeEditorOptions> {
        private readonly minTime: string;
        private readonly maxTime: string;
        private readonly step: number = 60;

        constructor(input: JQuery, opt?: TimeEditorOptions) {
            super(input, opt);

            if (opt.minTime && typeof opt.minTime === "string") {
                const minTimeArray = opt.minTime.split(/[:.]/),
                    minTimeHour = +minTimeArray[0],
                    minTimeMin = +minTimeArray[1];

                if (minTimeHour != null && minTimeMin != null) {
                    this.minTime = `${Q.zeroPad(minTimeHour, 2)}:${Q.zeroPad(+minTimeMin, 2)}`;
                }
            }

            if (opt.maxTime && typeof opt.maxTime === "string") {
                const maxTimeArray = opt.maxTime.split(/[:.]/),
                    maxTimeHour = +maxTimeArray[0],
                    maxTimeMin = +maxTimeArray[1];

                if (maxTimeHour != null && maxTimeMin != null) {
                    this.maxTime = `${Q.zeroPad(maxTimeHour, 2)}:${Q.zeroPad(+maxTimeMin, 2)}`;
                }
            }

            // if (opt.intervalMinutes && (typeof opt.intervalMinutes === "number" && opt.intervalMinutes > 1)) {
            //     this.step *= opt.intervalMinutes;
            // }

            this.set_attributes();
            const self = this;

            /* Re format value - hh:mm */
            this.element.on("change", function (event: JQueryEventObject) {
                // this.element.removeAttr("step");
                self.set_value(Q.parseDayHourAndMin(this.value.substr(0, 5)));
            });
        }

        public get value(): number | null {
            this.set_attributes();
            const value = this.element.val();

            if (!value) {
                return null;
            }

            const timeArray = value.split(/[:.]/);

            return timeArray[0] * 60 + (+timeArray[1]);
        }

        public set value(value: number) {
            this.set_attributes();

            if (!value) {
                return;
            }

            // @ts-ignore
            const hour = Q.zeroPad(Math.trunc(value / 60), 2),
                minuets = Q.zeroPad(value % 60, 2);

            this.element.val(`${hour}:${minuets}`);
        }

        protected get_value(): number {
            return this.value;
        }

        protected set_value(value: number): void {
            this.value = value;
        }

        private set_attributes(): void {
            this.element.addClass("hourAndMin");

            if (this.minTime) {
                this.element.attr("min", this.minTime);
            }

            if (this.maxTime) {
                this.element.attr("max", this.maxTime);
            }

            // this.element.attr("step", this.step);
        }
    }

    @Editor("URL", [IStringValue])
    export class URLEditor extends StringEditor {
        constructor(input: JQuery) {
            super(input);

            input.addClass("url").attr("title", "URL should be entered in format: 'http://www.example.com/page'.");

            input.on(`blur.${this.uniqueName}`, e => {
                var validator = input.closest("form").data("validator") as JQueryValidation.Validator;

                if (validator == null) {
                    return;
                }

                if (!input.hasClass("error")) {
                    return;
                }

                var value = Q.trimToNull(input.val());

                if (!value) {
                    return;
                }

                value = `http://${value}`;

                if ($.validator.methods["url"].call(validator, value, input[0]) == true) {
                    input.val(value);
                    validator.element(input);
                }
            });
        }
    }
}